import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentStatus() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { clear } = useCart();
  const sessionId = params.get("session_id");
  const isCancel = location.pathname.includes("cancel");
  const [state, setState] = useState(isCancel ? "cancelled" : "checking");
  const [kind, setKind] = useState(null);
  const [partnerPending, setPartnerPending] = useState(false);
  const tries = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (isCancel || !sessionId) return;
    let timer;
    const poll = async () => {
      tries.current += 1;
      try {
        const res = await api.paymentStatus(sessionId);
        setKind(res.kind);
        if (res.payment_status === "paid") {
          setState("paid");
          if (res.kind === "merch" && !cleared.current) { cleared.current = true; clear(); }
          if (res.kind === "subscription") {
            api.partnerOnboarding()
              .then((ob) => { if (ob?.has_business && ob?.partner_type && !ob?.onboarding_complete) setPartnerPending(true); })
              .catch(() => {});
          }
          return;
        }
        if (["failed", "expired"].includes(res.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (tries.current >= 12) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, isCancel]);

  const back = partnerPending ? "/partner/onboarding" : kind === "subscription" ? "/business" : kind === "ad_purchase" ? "/advertising/dashboard" : kind === "merch" ? "/shop" : "/marketplace";
  const isAd = kind === "ad_purchase";
  const isMerch = kind === "merch";

  return (
    <div className="min-h-screen bg-forest grain flex items-center justify-center px-6" data-testid="payment-status-page">
      <div className="bg-paper max-w-md w-full p-10 text-center">
        {state === "checking" && (
          <><Loader2 size={48} className="mx-auto text-forest animate-spin" /><h1 className="font-serif text-2xl text-forest mt-4">Confirming your payment…</h1><p className="text-charcoal/60 text-sm mt-2">Hold tight, this only takes a moment.</p></>
        )}
        {state === "paid" && (
          <><CheckCircle2 size={56} className="mx-auto text-[#2f7d4f]" data-testid="payment-success-icon" /><h1 className="font-serif text-3xl text-forest mt-4">{isAd ? "Welcome to HomeRange Advertising" : "You're all set!"}</h1><p className="text-charcoal/60 text-sm mt-2">{isAd ? "Your advertising package has been purchased successfully. Please complete your advertising profile so we can activate your placement." : partnerPending ? "Your partner subscription is active. Let's finish your profile so hunters can find you." : kind === "subscription" ? "Your HomeRange Business subscription is active. Time to build your storefront." : isMerch ? "Your HomeRange merch order is confirmed — a receipt is on its way to your email. We'll send tracking once it ships." : "Your order is confirmed. The outfitter has been notified."}</p></>
        )}
        {(state === "cancelled" || state === "failed" || state === "timeout") && (
          <><XCircle size={56} className="mx-auto text-destructive" data-testid="payment-failed-icon" /><h1 className="font-serif text-3xl text-forest mt-4">{state === "cancelled" ? "Checkout cancelled" : "Payment not completed"}</h1><p className="text-charcoal/60 text-sm mt-2">No charge was made. You can try again anytime.</p></>
        )}
        <div className="mt-6 flex gap-2 justify-center">
          <button onClick={() => navigate(back)} className="bg-forest text-bone px-5 py-2.5 text-sm font-semibold hover:bg-forest-2 transition-colors" data-testid="payment-continue-btn">{isAd ? "Complete Advertising Setup" : partnerPending ? "Continue partner setup" : kind === "subscription" ? "Go to dashboard" : isMerch ? "Back to store" : "Back to marketplace"}</button>
          {isMerch && <button onClick={() => navigate("/returns")} className="border border-saddle/30 text-charcoal px-5 py-2.5 text-sm font-semibold hover:border-forest transition-colors" data-testid="payment-returns-btn">Start a return</button>}
          <button onClick={() => navigate("/")} className="border border-saddle/30 text-charcoal px-5 py-2.5 text-sm font-semibold hover:border-forest transition-colors">Home</button>
        </div>
      </div>
    </div>
  );
}
